import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ParticleField, MorphBlob, DataStream } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";
import { 
  ArrowRight, 
  Calendar, 
  User, 
  FileText,
  BookOpen,
  Briefcase,
  ExternalLink,
  Rss,
  RefreshCw,
  Clock,
  Send,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const categoryIcons = {
  blog: BookOpen,
  whitepaper: FileText,
  case_study: Briefcase
};

const categoryColors = {
  blog: "#00B4D8",
  whitepaper: "#8B5CF6",
  case_study: "#10B981"
};

const newsCategoryColors = {
  "General Procurement": "#00B4D8",
  "AI in Procurement": "#8B5CF6",
  "Spend Analytics": "#10B981",
  "Contract Management": "#F59E0B",
  "Supply Chain Risk": "#EF4444",
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "";
  }
};

const IndustryIntelSection = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchNews = useCallback(async (cat) => {
    try {
      const params = cat && cat !== "all" ? `?category=${encodeURIComponent(cat)}` : "";
      const res = await axios.get(`${API}/industry-news${params}`);
      setArticles(res.data.articles || []);
      setCategories(res.data.categories || []);
      setLastRefreshed(res.data.last_refreshed);
    } catch {
      // Silently fail - articles section will show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews(selectedCategory);
  }, [selectedCategory, fetchNews]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/industry-news/refresh`);
      await fetchNews(selectedCategory);
      toast.success("Industry news refreshed!");
    } catch {
      toast.error("Failed to refresh news");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      {/* Category filter + refresh */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedCategory === "all"
                ? "bg-[#00B4D8] text-[#020617]"
                : "bg-[#0B1426] text-[#94A3B8] hover:text-[#F8FAFC] border border-[#00B4D8]/20"
            }`}
            data-testid="news-filter-all"
          >
            All Topics
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedCategory === cat
                  ? "text-[#020617]"
                  : "bg-[#0B1426] text-[#94A3B8] hover:text-[#F8FAFC] border border-[#00B4D8]/20"
              }`}
              style={selectedCategory === cat ? { backgroundColor: newsCategoryColors[cat] || "#00B4D8" } : {}}
              data-testid={`news-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-[#64748B] flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {timeAgo(lastRefreshed)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-[#00B4D8]/30 text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111F36]"
            data-testid="news-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Articles grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-[#111F36] rounded-xl p-6 animate-pulse">
              <div className="h-3 bg-[#1a2d4a] rounded w-1/4 mb-4" />
              <div className="h-5 bg-[#1a2d4a] rounded w-3/4 mb-3" />
              <div className="h-3 bg-[#1a2d4a] rounded w-full mb-2" />
              <div className="h-3 bg-[#1a2d4a] rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16">
          <Rss className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
          <p className="text-[#94A3B8] mb-4">No articles found. Click refresh to pull the latest industry news.</p>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4]"
            data-testid="news-empty-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Fetch Articles
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {articles.map((article, index) => {
              const catColor = newsCategoryColors[article.category] || "#00B4D8";
              return (
                <motion.a
                  key={article.id || article.url}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className="group block bg-[#111F36] border border-[#00B4D8]/10 rounded-xl p-6 hover:border-[#00B4D8]/30 hover:shadow-[0_0_25px_rgba(0,180,216,0.06)] transition-all"
                  data-testid={`news-article-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                      style={{ backgroundColor: `${catColor}15`, color: catColor }}
                    >
                      {article.category}
                    </span>
                    <span className="text-[10px] text-[#64748B]">{article.source}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#F8FAFC] mb-2 group-hover:text-[#00B4D8] transition-colors leading-snug line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed mb-4 line-clamp-3">
                    {article.summary}
                  </p>
                  <div className="flex items-center justify-between">
                    {article.published_raw && (
                      <span className="text-[10px] text-[#64748B]">{timeAgo(article.published_raw)}</span>
                    )}
                    <span className="text-[#00B4D8] text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all ml-auto">
                      Read original <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export const InsightsPage = () => {
  const [activeTab, setActiveTab] = useState("intel");
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const response = await axios.get(`${API}/blog`);
      if (response.data.length === 0) {
        await axios.post(`${API}/seed-blog`);
        const seeded = await axios.get(`${API}/blog`);
        setPosts(seeded.data);
      } else {
        setPosts(response.data);
      }
    } catch {
      // Silently fail - blog section will show empty state
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "blog" && posts.length === 0) {
      fetchPosts();
    }
  }, [activeTab, posts.length, fetchPosts]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thank you for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  const filteredPosts = selectedCategory === "all" 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const blogCategories = [
    { id: "all", name: "All" },
    { id: "blog", name: "Blog" },
    { id: "whitepaper", name: "Whitepapers" },
    { id: "case_study", name: "Case Studies" }
  ];

  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Insights"
        description="Procurement thought leadership, AI industry intel and expert analysis from the ProcureAide team. Stay ahead of trends in spend analytics, contract management and supply chain risk."
        path="/insights"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={18} />
        <MorphBlob color="rgba(0,180,216,0.06)" size="450px" className="top-[10%] right-[-10%]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              Insights & Resources
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              Procurement Intelligence{" "}
              <span className="text-[#00B4D8]">Hub</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8]"
            >
              AI-curated industry news and thought leadership on procurement, 
              spend analytics, and supply chain innovation.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="border-b border-[#00B4D8]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("intel")}
              className={`px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === "intel"
                  ? "border-[#00B4D8] text-[#00B4D8]"
                  : "border-transparent text-[#94A3B8] hover:text-[#F8FAFC]"
              }`}
              data-testid="tab-industry-intel"
            >
              <Rss className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Industry Intel
            </button>
            <button
              onClick={() => setActiveTab("blog")}
              className={`px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === "blog"
                  ? "border-[#00B4D8] text-[#00B4D8]"
                  : "border-transparent text-[#94A3B8] hover:text-[#F8FAFC]"
              }`}
              data-testid="tab-our-insights"
            >
              <BookOpen className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Our Insights
            </button>
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === "intel" ? (
            <IndustryIntelSection />
          ) : (
            <>
              {/* Blog category filter */}
              <div className="flex flex-wrap gap-4 justify-center mb-10">
                {blogCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === category.id
                        ? "bg-[#00B4D8] text-[#020617]"
                        : "bg-[#0B1426] text-[#94A3B8] hover:text-[#F8FAFC] border border-[#00B4D8]/20"
                    }`}
                    data-testid={`filter-${category.id}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Posts grid */}
              {postsLoading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="blog-card animate-pulse">
                      <div className="h-48 bg-[#111F36]" />
                      <div className="p-6">
                        <div className="h-4 bg-[#111F36] rounded w-1/4 mb-4" />
                        <div className="h-6 bg-[#111F36] rounded w-3/4 mb-3" />
                        <div className="h-4 bg-[#111F36] rounded w-full mb-2" />
                        <div className="h-4 bg-[#111F36] rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-[#94A3B8]">No posts found in this category.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.map((post, index) => {
                    const CategoryIcon = categoryIcons[post.category] || BookOpen;
                    const catColor = categoryColors[post.category] || "#00B4D8";
                    
                    return (
                      <motion.div
                        key={post.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Link 
                          to={`/insights/${post.slug}`}
                          className="block blog-card h-full group"
                          data-testid={`blog-post-${post.slug}`}
                        >
                          <div 
                            className="h-48 flex items-center justify-center"
                            style={{ backgroundColor: `${catColor}10` }}
                          >
                            <CategoryIcon className="w-16 h-16 opacity-30" style={{ color: catColor }} />
                          </div>
                          <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <span 
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${catColor}20`, color: catColor }}
                              >
                                {post.category.replace("_", " ")}
                              </span>
                              <span className="text-xs text-[#64748B] flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2 group-hover:text-[#00B4D8] transition-colors">
                              {post.title}
                            </h3>
                            <p className="text-sm text-[#94A3B8] mb-4 line-clamp-2">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-[#64748B] flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {post.author}
                              </span>
                              <span className="text-[#00B4D8] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                Read more <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <DataStream count={4} className="opacity-20" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Stay Updated
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Subscribe to receive the latest insights on procurement AI and best practices.
            </motion.p>
            <motion.form 
              variants={fadeInUp}
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC] placeholder:text-[#64748B]"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                disabled={subscribing}
                className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4]"
                data-testid="newsletter-submit-btn"
              >
                {subscribing ? "..." : <><Send className="w-4 h-4 mr-2" /> Subscribe</>}
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
